/**
 * Inactive Status Utility
 *
 * Determines inactivity status for connections based on last activity or deletion.
 * Pure utility — no hooks, no side effects, no DB queries.
 *
 * Anon connections: timer-based (meetingDate / last Anon message).
 * Deleted Profile connections: immediate inactive via peerProfileDeleted flag.
 *
 * Statuses:
 * - 'active'         — Anon: < 24h since anchor. Profile: account exists.
 * - 'soft_inactive'  — Anon only: 24h–7d since anchor.
 * - 'inactive'       — Anon: 7d+ since anchor. Profile: account deleted.
 * - null             — not applicable (PXP with active account, manual, converted Anon with active account)
 */

import { getConnectionDisplayName } from './connectionName';
import { isPeerGone } from './peerGone';
// Thresholds live with the rest of the lifecycle windows (incl. the DEBUG_TIMERS toggle)
import { SOFT_INACTIVE_MS, INACTIVE_MS } from './lifecycleWindows';

/**
 * Get the inactivity status of a connection.
 *
 * @param {object} connection — connection object with connectionMetadata, anonData, chatMessages, isManualContact
 * @returns {'active'|'soft_inactive'|'inactive'|null} — null means inactivity logic doesn't apply
 */
export const getInactiveStatus = (connection) => {
  if (connection.isManualContact) return null;

  // ORDERING MATTERS: the peer-gone check must run BEFORE the exchangeType and
  // senderProfileUserId guards below. A gone peer — account-deleted OR left this
  // connection (isPeerGone, CD #15), incl. a PXP or converted-Anon peer — should
  // show as inactive; without this ordering the guards would return null first.
  if (isPeerGone(connection.anonData)) return 'inactive';

  if (connection.connectionMetadata?.exchangeType === 'pxp') return null;
  if (connection.connectionMetadata?.exchangeType !== 'anon_profile') return null;
  if (connection.anonData?.senderProfileUserId) return null;

  const meetingDate = connection.connectionMetadata?.meetingDate;
  if (!meetingDate) return null;

  // Find the latest Anon-sent chat message timestamp
  let lastAnonMessageMs = 0;
  const messages = connection.chatMessages;
  if (Array.isArray(messages)) {
    for (const msg of messages) {
      if (msg.sender === 'anon' && msg.type === 'chat' && msg.timestamp) {
        const msgMs = new Date(msg.timestamp).getTime();
        if (msgMs > lastAnonMessageMs) lastAnonMessageMs = msgMs;
      }
    }
  }

  // Anchor: later of meetingDate and last Anon message
  const meetingMs = new Date(meetingDate).getTime();
  const anchorMs = Math.max(meetingMs, lastAnonMessageMs);
  const ageMs = Date.now() - anchorMs;

  if (ageMs < SOFT_INACTIVE_MS) return 'active';
  if (ageMs < INACTIVE_MS) return 'soft_inactive';
  return 'inactive';
};

/**
 * Get a display-safe first name for inactive connection banner messages.
 *
 * @param {object} connection — connection object
 * @returns {string}
 */
export const getBannerDisplayName = (connection) => {
  const fullName = getConnectionDisplayName(connection) || '';
  if (!fullName) return 'This person';
  return fullName.split(' ')[0];
};
